import { FC } from 'react';

import { useTranslate } from 'hooks/useTranslate';
import { Link } from 'react-router-dom';

import {
  formatCurrencyDecimals,
  formatNumber,
  formatNumberDecimals,
} from 'utils/formats';

import TableRow from 'modules/_shared/Table/components/TableRow';
import { RowCell } from 'modules/_shared/Table/types/RowCell';
import { format } from 'date-fns';
import { dateFormat } from 'constants/formats';
import { Eligible } from '../types/Eligible';

type EligiblesTableRowProps = {
  index: number;
  eligible: Eligible;
  isReadOnly?: boolean;
};

const EligiblesTableRow: FC<EligiblesTableRowProps> = ({
  index,
  eligible,
  isReadOnly = false,
}) => {
  const { t } = useTranslate();

  const cells = [
    {
      key: `index-${index}`,
      content: index + 1,
    },

    {
      key: `name-${index}`,
      content: (
        <div className="tb-tnx-info w-150px">
          {isReadOnly ? (
            <span className="title">{eligible.name}</span>
          ) : (
            <Link to={eligible.link}>
              <span className="title">{eligible.name}</span>
            </Link>
          )}
        </div>
      ),
    },

    {
      key: `totalUnits-${index}`,
      content: formatNumber(eligible.totalUnits),
      textPosition: 'right',
      hideOnMobile: true,
    },

    {
      key: `maximumSaleUnits-${index}`,
      content: formatNumber(eligible.maximumSaleUnits),
      textPosition: 'right',
    },

    {
      key: `ndPercentage-${index}`,
      content: `${formatNumberDecimals(eligible.ndPercentage)}%`,
      textPosition: 'right',
      hideOnMobile: true,
    },

    {
      key: `fdPercentage-${index}`,
      content: `${formatNumberDecimals(eligible.fdPercentage)}%`,
      textPosition: 'right',
      hideOnMobile: true,
    },

    {
      key: `offerAmount-${index}`,
      content: formatCurrencyDecimals(eligible.offerAmount),
      textPosition: 'right',
      hideOnMobile: true,
    },

    {
      key: `soldUnits-${index}`,
      content: formatNumber(eligible.soldUnits),
      textPosition: 'right',
    },

    {
      key: `soldAmount-${index}`,
      content: formatCurrencyDecimals(eligible.soldAmount),
      textPosition: 'right',
    },

    {
      key: `strikePrice-${index}`,
      content: formatCurrencyDecimals(eligible.strikePrice),
      textPosition: 'right',
    },

    {
      key: `retentionPercentage-${index}`,
      content: `${formatNumberDecimals(eligible.retentionPercentage)}%`,
      textPosition: 'right',
    },

    {
      key: `netAmount-${index}`,
      content: formatCurrencyDecimals(eligible.netAmount),
      textPosition: 'right',
    },

    {
      key: `saleDate-${index}`,
      content: eligible.saleDate ? format(eligible.saleDate, dateFormat) : '-',
      textPosition: 'center',
    },

    {
      key: `status-${index}`,
      content: (
        <span className={`badge badge-check ${eligible.statusBadge}`}>
          {t(eligible.statusLabel)}
        </span>
      ),
      textPosition: 'center',
    },
  ].filter(Boolean) as RowCell[];

  return <TableRow cells={cells} />;
};

export default EligiblesTableRow;
